<template>
  <div class="service-page">
    <!-- Carousel Hero Section -->
    <ServicesItsCarouselComp 
      :slides="electricBikeSlides"
      background-image="/resource/portfolio-02.jpg"
      pattern-image="/shapes/shape-06.png"
      :mission-vision="electricBikeMissionVision"
    />

    <!-- Electric Bike Innovation Section -->
    <ServiceItsCardNormal
      :header-data="ebikeInnovationHeaderData"
      :innovation-data="ebikeInnovations"
    />



    <!-- E-Bike Performance Gallery -->
    <ServiceItsNormalGalleryComp
      :header-data="performanceGalleryHeaderData"
      :gallery-data="performanceGallery"
    />
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import WOW from 'wowjs'
import ServicesItsCarouselComp from '@/components/ServicesItsCarouselComp.vue'
import ServiceItsCardNormal from '@/components/ServiceItsCardNormal.vue'
import ServiceItsNormalGalleryComp from '@/components/ServiceItsNormalGalleryComp.vue'

export default {
  name: 'ElectricBikeServicesPage',
  components: {
    ServicesItsCarouselComp,
    ServiceItsCardNormal,
    ServiceItsNormalGalleryComp
  },
  setup() {
    const ebikeInnovations = ref([
      {
        icon: 'fas fa-battery-full',
        title: 'Smart Battery Management',
        description: 'Advanced battery monitoring and optimization systems for maximum range and longevity.',
        features: ['Battery Health Monitoring', 'Smart Charging', 'Range Optimization', 'Cell Balancing']
      },
      {
        icon: 'fas fa-microchip',
        title: 'AI-Powered Diagnostics',
        description: 'Machine learning algorithms for predictive maintenance and performance optimization.',
        features: ['Predictive Analytics', 'Fault Detection', 'Performance Tuning', 'Smart Alerts']
      },
      {
        icon: 'fas fa-wifi',
        title: 'IoT Connectivity',
        description: 'Connected e-bike systems with real-time monitoring and remote diagnostics.',
        features: ['Real-time Monitoring', 'Remote Diagnostics', 'OTA Updates', 'Fleet Management']
      },
      {
        icon: 'fas fa-leaf',
        title: 'Eco-Friendly Solutions',
        description: 'Sustainable maintenance practices and green technology integration.',
        features: ['Green Materials', 'Energy Efficiency', 'Carbon Footprint', 'Recycling Programs']
      },
      {
        icon: 'fas fa-mobile-alt',
        title: 'Mobile Integration',
        description: 'Seamless smartphone integration for enhanced user experience and control.',
        features: ['App Control', 'GPS Tracking', 'Ride Analytics', 'Social Features']
      },
      {
        icon: 'fas fa-shield-alt',
        title: 'Security Systems',
        description: 'Advanced security features and anti-theft protection for your e-bike.',
        features: ['GPS Tracking', 'Alarm Systems', 'Lock Integration', 'Recovery Services']
      }
    ])

    const smartTechFeatures = ref([
      {
        icon: 'fas fa-brain',
        title: 'AI Diagnostics',
        description: 'Intelligent fault detection and predictive maintenance using machine learning.'
      },
      {
        icon: 'fas fa-chart-line',
        title: 'Performance Analytics',
        description: 'Real-time performance monitoring and optimization recommendations.'
      },
      {
        icon: 'fas fa-cloud',
        title: 'Cloud Integration',
        description: 'Seamless data synchronization and remote access to your e-bike systems.'
      }
    ])

    const techOrbits = ref([
      { icon: 'fas fa-battery-three-quarters', delay: '0s' },
      { icon: 'fas fa-cog', delay: '1s' },
      { icon: 'fas fa-wifi', delay: '2s' },
      { icon: 'fas fa-mobile-alt', delay: '3s' }
    ])

    const performanceGallery = ref([
      {
        image: require('@/assets/resource/portfolio-01.jpg'),
        title: 'Battery Range Enhancement',
        description: 'Increased range by 40% through advanced battery optimization.',
        stats: [
          { value: '+40%', label: 'Range Increase' },
          { value: '85km', label: 'New Range' },
          { value: '2.5hrs', label: 'Charge Time' }
        ]
      },
      {
        image: require('@/assets/resource/portfolio-02.jpg'),
        title: 'Motor Performance Boost',
        description: 'Enhanced motor efficiency and power output for better performance.',
        stats: [
          { value: '+25%', label: 'Power Boost' },
          { value: '500W', label: 'Peak Power' },
          { value: '95%', label: 'Efficiency' }
        ]
      },
      {
        image: require('@/assets/resource/portfolio-03.jpg'),
        title: 'Smart System Integration',
        description: 'Complete IoT integration for intelligent e-bike management.',
        stats: [
          { value: '24/7', label: 'Monitoring' },
          { value: '99.9%', label: 'Uptime' },
          { value: '5G', label: 'Connectivity' }
        ]
      },
      {
        image: require('@/assets/resource/portfolio-04.jpg'),
        title: 'Eco-Friendly Upgrade',
        description: 'Sustainable materials and green technology implementation.',
        stats: [
          { value: '-60%', label: 'Carbon Footprint' },
          { value: '100%', label: 'Recyclable' },
          { value: 'Zero', label: 'Waste' }
        ]
      }
    ])

    onMounted(async () => {
      // Initialize WOW.js
      new WOW.WOW({
        mobile: false
      }).init()

      // Import and initialize Owl Carousel
      try {
        await import('owl.carousel')
        
        // Initialize Owl Carousel for banner
        setTimeout(() => {
          if (window.$ && window.$.fn.owlCarousel) {
            window.$('.banner-carousel').owlCarousel({
              loop: true,
              margin: 0,
              nav: true,
              active: true,
              smartSpeed: 1000,
              autoplay: 6000,
              navText: ['<span class="icon-05"></span>', '<span class="icon-06"></span>'],
              responsive: {
                0: {
                  items: 1
                },
                600: {
                  items: 1
                },
                768: {
                  items: 1
                },
                992: {
                  items: 1
                },
                1200: {
                  items: 1
                }
              }
            })
          }
        }, 100)
      } catch (error) {
        console.error('Error loading Owl Carousel:', error)
      }
    })

    // Electric Bike Services slides data
    const electricBikeSlides = ref([
      {
        subtitle: 'Electric Bike Services',
        titleLines: [
          { text: 'Professional ', highlight: false },
          { text: 'Electric Bike ', highlight: 'Care' }
        ],
        description: 'Specialized electric bike services for all types of e-bikes. From battery maintenance to motor repairs, we provide expert care for your electric bicycle.',
        buttonText: 'Get Service',
        buttonLink: '/contact'
      },
      {
        subtitle: 'Battery Specialists',
        titleLines: [
          { text: 'Expert ', highlight: 'Technicians' }
        ],
        description: 'Our certified technicians specialize in electric bike systems, battery technology, and motor maintenance to keep your e-bike running efficiently.',
        buttonText: 'Learn More',
        buttonLink: '/contact'
      },
      {
        subtitle: 'Eco-Friendly Service',
        titleLines: [
          { text: 'Sustainable ', highlight: 'Solutions' }
        ],
        description: 'We offer comprehensive electric bike services including battery diagnostics, motor repair, charging system maintenance, and performance optimization.',
        buttonText: 'Book Now',
        buttonLink: '/contact'
      }
    ]);

    // Electric Bike Services mission/vision data
    const electricBikeMissionVision = ref({
      vision: {
        title: 'Our Vision',
        description: 'To be the leading electric bike service provider offering cutting-edge solutions for sustainable transportation with unmatched expertise and customer satisfaction.'
      },
      mission: {
        title: 'Our Mission',
        description: 'To deliver exceptional electric bike services using advanced diagnostic tools, specialized knowledge, and eco-friendly practices to ensure optimal e-bike performance and longevity.'
      }
    });

    // Header data for components
    const ebikeInnovationHeaderData = {
      icon: "fas fa-bolt",
      tag: "ELECTRIC INNOVATION",
      title: "Next-Gen E-Bike Services",
      description: "Revolutionary electric bike maintenance with smart technology integration"
    }

    const performanceGalleryHeaderData = {
      icon: "fas fa-chart-line",
      tag: "PERFORMANCE GALLERY",
      title: "E-Bike Performance Showcase",
      description: "See our e-bike transformations and performance improvements"
    }

    return {
      ebikeInnovations,
      smartTechFeatures,
      techOrbits,
      performanceGallery,
      electricBikeSlides,
      electricBikeMissionVision,
      ebikeInnovationHeaderData,
      performanceGalleryHeaderData
    }
  }
}
</script>

<style scoped>
/* Import Google Fonts */
@import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,300;1,400;1,500;1,600;1,700;1,800&display=swap');

/* Custom button styling for carousel - More specific to avoid conflicts */
.service-page .banner__two .btn-box .theme-btn {
  padding: 12px 30px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: uppercase !important;
  letter-spacing: 1px !important;
  border-radius: 0 !important;
  background: linear-gradient(219deg, #FB0E76 0%, #910694 100%) !important;
  color: #fff !important;
  text-decoration: none !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 8px !important;
  transition: all 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 5px 15px rgba(251, 14, 118, 0.4) !important;
  color: #fff !important;
  text-decoration: none !important;
}

.service-page .banner__two .btn-box .theme-btn i {
  font-size: 12px !important;
  transition: transform 0.3s ease !important;
}

.service-page .banner__two .btn-box .theme-btn:hover i {
  transform: translateX(3px) !important;
}

/* Mission/Vision block styling */
.banner__mission__block {
  border-radius: 10px 0px 0px 0px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin-top: 20px;
}

.mission__block {
  padding: 40px 50px;
  border-right: 1px solid rgba(255, 255, 255, 0.10);
  flex: 1;
}

.mission__block:last-child {
  border-right: none;
}

.mission__block h3 {
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.mission__block p {
  color: #ccc;
  font-size: 14px;
  line-height: 24px;
  margin: 0;
  font-family: 'Open Sans', sans-serif;
}

/* Reduce banner height - More specific to avoid conflicts */
.service-page section.banner__two {
  padding-top: 100px !important;
  padding-bottom: 100px !important;
}

.service-page .banner__two .content-box {
  padding-bottom: 60px !important;
}

/* Responsive adjustments - More specific to avoid conflicts */
@media (max-width: 768px) {
  .service-page .banner__two .content-box h2 {
    font-size: 50px !important;
    line-height: 60px !important;
  }
  
  .service-page .mission__block {
    padding: 30px 25px !important;
  }
  
  .service-page .mission__block h3 {
    font-size: 18px !important;
  }
  
  .service-page .mission__block p {
    font-size: 13px !important;
    line-height: 22px !important;
  }
}

/* Import Flaticon */
@import '@/assets/scss/Flaticon.scss';

/* Global Styles */
body {
  font-family: 'Open Sans', sans-serif;
  line-height: 1.6;
  color: #333;
}

.see__pad {
  padding: 100px 0;
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

/* Pattern Layers */
.pattern-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.pattern-1 {
  top: 10%;
  right: 5%;
  width: 100px;
  height: 100px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-2 {
  bottom: 20%;
  left: 8%;
  width: 80px;
  height: 80px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-3 {
  top: 30%;
  left: 10%;
  width: 120px;
  height: 120px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-4 {
  top: 15%;
  right: 15%;
  width: 90px;
  height: 90px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-5 {
  bottom: 25%;
  right: 20%;
  width: 70px;
  height: 70px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

.pattern-6 {
  top: 40%;
  left: 5%;
  width: 110px;
  height: 110px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.09;
}

.pattern-7 {
  bottom: 15%;
  left: 12%;
  width: 85px;
  height: 85px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.08;
}

.pattern-8 {
  top: 35%;
  right: 8%;
  width: 95px;
  height: 95px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.1;
}

/* Intro Section */
.service-intro {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.service-intro-content {
  position: relative;
  z-index: 2;
}

.service-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.service-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.service-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

/* Services Offered Section */
.services-offered {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.services-header {
  position: relative;
  z-index: 2;
  margin-bottom: 60px;
}

.services-section-label h4 {
  font-size: 20px;
  font-weight: 400;
  letter-spacing: 13.2px;
  color: #F6EC4F;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-transform: uppercase;
}

.services-main-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.services-description p {
  font-size: 18px;
  color: #fff;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.8;
}

.service-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(246, 236, 79, 0.1);
  border-radius: 15px;
  padding: 40px 30px;
  height: 100%;
  position: relative;
  transition: all 0.3s ease;
  overflow: hidden;
}

.service-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(246, 236, 79, 0.2);
  border-color: #F6EC4F;
}

.service-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.block-shape-1 {
  position: absolute;
  top: -20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, rgba(246, 236, 79, 0.1), rgba(246, 236, 79, 0.05));
  border-radius: 50%;
  opacity: 0.3;
}

.block-shape-2 {
  position: absolute;
  bottom: -30px;
  left: -30px;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, rgba(246, 236, 79, 0.08), rgba(246, 236, 79, 0.03));
  border-radius: 50%;
  opacity: 0.4;
}

.service-content {
  position: relative;
  z-index: 2;
}

.service-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
}

.service-icon i {
  font-size: 24px;
  color: #00061A;
}

.service-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.service-content p {
  font-size: 16px;
  color: #fff;
  margin-bottom: 20px;
  line-height: 1.6;
}

.service-content ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-content ul li {
  color: #fff;
  font-size: 14px;
  margin-bottom: 8px;
  padding-left: 20px;
  position: relative;
}

.service-content ul li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #F6EC4F;
  font-weight: bold;
}

/* Call to Action Section */
.service-cta {
  background: #00061A;
  position: relative;
  overflow: hidden;
}

.cta-content {
  position: relative;
  z-index: 2;
}

.cta-title h2 {
  font-size: 48px;
  font-weight: 700;
  line-height: 58px;
  color: #fff;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.cta-description p {
  font-size: 18px;
  color: #fff;
  max-width: 600px;
  margin: 0 auto 40px;
  line-height: 1.8;
}

.cta-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 15px 30px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.primary-btn {
  background: linear-gradient(135deg, #F6EC4F, #E6D43A);
  color: #00061A;
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(246, 236, 79, 0.3);
}

.secondary-btn {
  background: transparent;
  color: #F6EC4F;
  border-color: #F6EC4F;
}

.secondary-btn:hover {
  background: #F6EC4F;
  color: #00061A;
  transform: translateY(-2px);
}

/* Responsive Design */
@media (max-width: 767px) {
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 36px;
    line-height: 46px;
  }
  
  .service-section-label h4,
  .services-section-label h4 {
    letter-spacing: 8px;
    font-size: 16px;
  }
  
  .service-item {
    padding: 30px 20px;
  }
  
  .cta-buttons {
    flex-direction: column;
    align-items: center;
  }
  
  .cta-btn {
    width: 100%;
    max-width: 300px;
    justify-content: center;
  }
}

@media (max-width: 399px) {
  .see__pad {
    padding: 60px 0;
  }
  
  .service-main-title h2,
  .services-main-title h2,
  .cta-title h2 {
    font-size: 28px;
    line-height: 38px;
  }
}

/* Electric Bike Innovation Section - Now handled by ServiceItsCardNormal component */


.innovation-header {
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 2;
}

.innovation-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
}

.innovation-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: white;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.innovation-subtitle {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.innovation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 50px;
  position: relative;
  z-index: 2;
}

.innovation-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 40px 30px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.innovation-card:hover {
  transform: translateY(-10px);
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(0, 212, 255, 0.5);
  box-shadow: 0 20px 40px rgba(0, 212, 255, 0.2);
}

.card-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 25px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(0, 212, 255, 0.3);
}

.innovation-card:hover .card-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 15px 30px rgba(0, 212, 255, 0.5);
}

.card-icon i {
  font-size: 32px;
  color: white;
}

.card-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: white;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.card-content p {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
  line-height: 1.6;
}

.tech-features {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tech-tag {
  background: rgba(0, 212, 255, 0.2);
  color: #00d4ff;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid rgba(0, 212, 255, 0.3);
}

.card-glow {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(0, 212, 255, 0.1) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.innovation-card:hover .card-glow {
  opacity: 1;
}

/* Smart Technology Showcase */
.smart-tech-showcase {
  padding: 100px 0;
  position: relative;
  overflow: hidden;
}

.showcase-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('@/assets/resource/portfolio-03.jpg') center/cover no-repeat;
  background-attachment: fixed;
}

.tech-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.9), rgba(0, 86, 179, 0.9));
}

.tech-content {
  position: relative;
  z-index: 2;
  color: white;
}

.tech-title {
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 25px;
  font-family: 'Archivo', sans-serif;
}

.tech-description {
  font-size: 18px;
  margin-bottom: 40px;
  line-height: 1.6;
  opacity: 0.9;
}

.tech-features-list {
  margin-bottom: 40px;
}

.tech-feature {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.tech-feature:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateX(10px);
}

.feature-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon i {
  font-size: 24px;
  color: white;
}

.feature-text h4 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  font-family: 'Archivo', sans-serif;
}

.feature-text p {
  font-size: 16px;
  opacity: 0.8;
  margin: 0;
}

.tech-btn {
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  color: white;
  border: none;
  padding: 15px 35px;
  border-radius: 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 25px rgba(0, 212, 255, 0.3);
}

.tech-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(0, 212, 255, 0.5);
}

.tech-visual {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.tech-circle {
  position: relative;
  width: 300px;
  height: 300px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle-center {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.5);
}

.circle-center i {
  font-size: 32px;
  color: white;
}

.tech-orbit {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  animation: orbit 10s linear infinite;
}

.orbit-item {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.orbit-item i {
  font-size: 14px;
  color: white;
}

@keyframes orbit {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Performance Gallery Section - Now handled by ServiceItsNormalGalleryComp component */

.gallery-header {
  text-align: center;
  margin-bottom: 60px;
}

.gallery-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  padding: 8px 20px;
  border-radius: 25px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
}

.gallery-header h2 {
  font-size: 48px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  font-family: 'Archivo', sans-serif;
}

.gallery-subtitle {
  font-size: 18px;
  color: #6c757d;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.performance-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.gallery-item {
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.gallery-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.gallery-image {
  position: relative;
  height: 250px;
  overflow: hidden;
}

.gallery-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-item:hover .gallery-image img {
  transform: scale(1.1);
}

.gallery-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.9), rgba(32, 201, 151, 0.9));
  color: white;
  padding: 30px;
  opacity: 0;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gallery-item:hover .gallery-overlay {
  opacity: 1;
}

.overlay-content {
  text-align: center;
}

.overlay-content h3 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 15px;
  font-family: 'Archivo', sans-serif;
}

.overlay-content p {
  font-size: 16px;
  margin-bottom: 25px;
  line-height: 1.6;
  opacity: 0.9;
}

.performance-stats {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
}

.stat {
  text-align: center;
}

.stat-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: #F6EC4F;
  margin-bottom: 5px;
  font-family: 'Archivo', sans-serif;
}

.stat-label {
  font-size: 12px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Responsive Design for New Sections */
@media (max-width: 768px) {
  .innovation-header h2,
  .tech-title,
  .gallery-header h2 {
    font-size: 36px;
  }
  
  .innovation-grid,
  .performance-gallery {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .tech-visual {
    height: 300px;
  }
  
  .tech-circle {
    width: 250px;
    height: 250px;
  }
  
  .performance-stats {
    flex-direction: column;
    gap: 15px;
  }
}
</style> 